import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import * as CountersActions from "./counters.actions";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {ResourceCounter} from '@association/_common/models/apartments.models';
import {ResourceType} from '@accounting/_common/models/_accounting.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const counterFeatureKey = 'counter';

const resourceTypeOrder: Record<ResourceType, number> = {
    'COLD_WATER': 0,
    'HOT_WATER': 1,
    'ELECTRICITY': 2,
    'HEATING': 3,
    'GAS': 4
};

export function sortCounters(a: ResourceCounter, b: ResourceCounter) {
    if (a.resourceType !== b.resourceType) {
        return resourceTypeOrder[a.resourceType] - resourceTypeOrder[b.resourceType];
    }

    if (a.name !== b.name) {
        return (a.name || '').localeCompare(b.name || '');
    }

    return 0;
}

export const adapter: EntityAdapter<ResourceCounter> = createEntityAdapter<ResourceCounter>({
    sortComparer: sortCounters
});

export type CounterState = EntityStateModel<ResourceCounter, any>;

export const initialState: CounterState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const counterReducer = createReducer(
    initialState,

    on(CountersActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(CountersActions.LoadListSuccess, (state, action) => {
        return adapter.setAll(action.payload.items, {
            ...state,
            pagination: action.payload.pagination,
            listLoading: undefined
        });
    }),

    on(CountersActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(CountersActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(CountersActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(CountersActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(CountersActions.UpdateItemSuccess, (state, action) => {
        return adapter.setOne(action.payload!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
        });
    }),

    on(CountersActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(CountersActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(CountersActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(CountersActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();
